<template>
  <div
    class="sui-uploader-mini"
    :class="{
      'sui-uploader-mini--disabled': disabled,
      'sui-uploader-mini--error': hasErrors,
    }"
  >
    <div
      v-if="!files.length"
      class="sui-uploader-mini__uploader"
      @click="onUploaderClick"
    >
      <SIcon name="document-clip" size="xl" class="sui-uploader-mini__uploader-icon" />
      <span class="sui-uploader-mini__uploader-label">{{ label }}</span>
    </div>
    <div v-else class="sui-uploader-mini__file">
      <FilePreview
        :file="files[0]"
        :sizes="{ width: 48, height: 48 }"
        @click="onPreviewClick(files[0])"
      />
      <div class="sui-uploader-mini__file-right">
        <div class="sui-uploader-mini__file-info">
          <div class="sui-uploader-mini__file-name">
            {{ files[0].name }}
          </div>
          <div class="sui-uploader-mini__file-size">
            {{ formatFileSize(files[0].size) }}
          </div>
        </div>
        <div class="sui-uploader-mini__file-actions">
          <SIcon v-if="downloadable" name="download" size="sm" @click="downloadFile(files[0])" />
          <SIcon v-if="removable" name="trash" size="sm" @click="removeFile" />
        </div>
      </div>
    </div>
    <FileSelectModal
      v-if="isFileSelectModalOpen"
      v-model:file="selectedFile"
      :accept="accept"
      :description="description"
      :drop-area-title="dropAreaTitle"
      :drop-area-button-text="dropAreaButtonText"
      @close="onFileSelectModalClose"
    />
    <FileViewer
      v-if="isFileViewerOpen"
      :label="viewerTitle"
      :files="[files[0]]"
      :removable="removable"
      :downloadable="downloadable"
      @hide-dialog="isFileViewerOpen = false"
      @remove-file="removeFile"
    />
    <FilePlugSprite />
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'

import { formatFileSize } from '../uploader/helper'
import { isRemoteFile, UploaderFile } from '@/types/uploader'

import SIcon from '@/components/icon/Icon.vue'
import FilePreview from '@/components/file/FilePreview.vue'
import FileViewer from '@/components/file/FileViewer.vue'
import FilePlugSprite from '@/components/file/FilePlugSprite.vue'
import FileSelectModal from './FileSelectModal.vue'

export type Props = {
  label: string
  description?: string
  accept?: string
  multiple?: boolean // TODO: загрузка нескольких файлов 
  viewerTitle?: string
  dropAreaTitle?: string
  dropAreaButtonText?: string
  removable?: boolean
  downloadable?: boolean
  disabled?: boolean
  hasErrors?: boolean
}

const files = defineModel('files', { type: Array<UploaderFile>, required: true })

withDefaults(defineProps<Props>(), {
  description: '',
  accept: '.jpeg,.jpg,.png,.pdf',
  multiple: false,
  viewerTitle: 'Просмотр файла',
  dropAreaTitle: 'Перетащите или выберете файл',
  dropAreaButtonText: 'Выбрать файл',
  removable: true,
  downloadable: true,
  disabled: false,
  hasErrors: false,
})

const emit = defineEmits<{
  'file-click': [{ file: UploaderFile; index: number }]
  'file-removed': [index: number]
}>()

const imageFormats = ['apng', 'gif', 'ico', 'cur', 'jpg', 'jpeg', 'pjpeg', 'pjp', 'png', 'svg', 'webp', 'bmp']

const isFileSelectModalOpen = ref(false)
const isFileViewerOpen = ref(false)
const selectedFile = ref<UploaderFile | null>(null)

function onUploaderClick() {
  selectedFile.value = null
  isFileSelectModalOpen.value = true
}

function onFileSelectModalClose() {
  if (selectedFile.value) {
    files.value = [selectedFile.value]
    selectedFile.value = null
  }
  isFileSelectModalOpen.value = false
}

function onPreviewClick(file: UploaderFile) {
  if (imageFormats.includes(getFileExtension(file.name)) || getFileExtension(file.name) === 'pdf') {
    viewFile(file)
  } else {
    downloadFile(file)
  }
}

function viewFile(file: UploaderFile) {
  emit('file-click', { file, index: 0 })
  isFileViewerOpen.value = true
}

function downloadFile(file: UploaderFile) {
  try {
    const anchor = document.createElement('a')
    anchor.href = isRemoteFile(file) ? file.link : URL.createObjectURL(file)
    anchor.download = file.name
    anchor.setAttribute('target', '_blank')
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  } catch (error: unknown) {
    console.warn(error instanceof Error ? error.message : error)
  }
}

function removeFile() {
  files.value = []
  emit('file-removed', 0)
  isFileViewerOpen.value = false
}

function getFileExtension(filename: string) {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase()
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/scss/mixins.scss';

.sui-uploader-mini {
  &__uploader {
    display: flex;
    align-items: center;
    gap: 12px;
    width: fit-content;
    cursor: pointer;
  }

  &__icon {
    color: var(--color-icon-primary);
  }

  &__label {
    font-size: 16px;
    line-height: 22px;
    color: var(--color-text-primary);
  }

  &__file {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background-color: var(--color-bg-secondary);
    border-radius: 8px;

    @include phone {
      padding: 8px;
    }

    &-right {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      overflow: hidden;

      @include phone {
        flex-direction: column;
        align-items: stretch;
        gap: 4px;
      }
    }

    &-info {
      overflow: hidden;
    }

    &-name {
      font-size: 16px;
      line-height: 22px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      @include phone {
        font-size: 14px;
        line-height: 20px;
        margin-bottom: 4px;
      }
    }

    &-size {
      font-size: 14px;
      line-height: 20px;
      color: var(--color-text-secondary);

      @include phone {
        font-size: 12px;
        line-height: 16px;
      }
    }

    &-actions {
      display: flex;
      align-items: center;
      gap: 12px; 
      margin-left: auto;
      color: var(--color-icon-secondary);

      & > * {
        cursor: pointer;
      }
    }
  }

  &--disabled {
    .sui-uploader-mini__uploader {
      pointer-events: none;

      &-icon {
        // FIXME: отказаться от свойства color в SIcon
        color: var(--color-icon-secondary) !important;
      }

      &-label {
        color: var(--color-text-secondary);
      }
    }
  }

  &--error {
    .sui-uploader-mini__uploader {
      &-icon {
        // FIXME: отказаться от свойства color в SIcon
        color: var(--color-icon-accent-dangerous) !important;
      }

      &-label {
        color: var(--color-text-accent);
      }
    }
  }
}
</style>
